/**
 * Issue #828 — the Position Tracker's working reading view.
 *
 * The allowlisted slice of "how the trader was reading their book" that survives
 * a departure and return within this page lifetime. Rides the same #824 baseline
 * (`workingViewStore` / `useWorkingView`) as `GameDetail/readingView.ts` and
 * `ValueDashboard/readingView.ts`, and obeys the same boundary: reading controls
 * only — never orders, arming, focus, tickets, market membership or account data.
 *
 * ## Why this slice is so small
 *
 * `/positions` is the most-persisted route in the app. Nine durable localStorage
 * keys plus one sessionStorage key already carry its filters, pins, pin view
 * mode, pin stickiness, four rail flags and the Open Orders sort across
 * navigation *and* reload. The risk here is the opposite of the Dashboard's:
 * not a missing preference layer, but a second one. So this file holds exactly
 * the one field an inventory proved had no owner at all.
 *
 * Deliberately NOT here, each because it already has a canonical owner this
 * layer must not become a second reader/writer of:
 *
 *  - sport + market-kind filters, positions and orders — `filterPersistence.ts`
 *  - pins, pins-collapsed, pins view mode, pins sticky — `pinsPersistence.ts`,
 *    `pinsViewMode.ts`, `pinsSticky.tsx`
 *  - the four rail hidden/expanded flags — `railPrefs.ts`
 *  - `positionViewMode`, `orderViewMode`, `showPnl` — `displaySettings`, the one
 *    canonical display-preference store (#691)
 *  - the Open Orders sort — `positionsOrderSortSession.ts`. It already survives
 *    navigation, and its per-tab sessionStorage lifetime was chosen on purpose:
 *    its header records the involuntary-remount bug (a reconnect/auth blip
 *    unmounting RequireAuth) that dropped a live Queue pick, and it deliberately
 *    permits the `queue`/`velocity` keys that `defaultPositionsOrderSort`
 *    coerces away on read. This store is in-memory, so it could not replace that
 *    — layering it on top would mean two owners and a mount-precedence rule for
 *    one field.
 *
 * Also not here, and not anywhere: `positionsLockKeys` / `ordersLockKeys` (#97
 * card-order lock), which #828 excludes by name, and the twelve execution and
 * confirmation states this page holds — edit/close/cancel dialogs, pending
 * cancellation, bulk-cancel snapshots, the buy panel. Existing execution and
 * recovery stores stay authoritative.
 *
 * ## There is no Reset/Undo slot, and that is a decision
 *
 * Every other working-view surface mounts `WorkingViewReset`. This one does not,
 * because here it would reset *less* than the control beside it.
 * `handlePositionSort` clears the #97 card-order lock before setting the sort —
 * "choosing a sort unlocks", visible in the Lock toggle. `reset()` cannot do
 * that: the lock sets are excluded from this slice by name. A button labelled
 * "Reset view" that left the card order frozen would be a weaker gesture wearing
 * a more authoritative label than the `Default` segment two controls to its
 * left. Both list modes already have a working back-to-default: the `Default`
 * sort segment in card/compact/mini, and the `Market` column header
 * (`sortKey="default"`) in table mode.
 *
 * ## No URL overlay
 *
 * `/positions` takes no query parameters and mounts once (`App.tsx`), so there
 * is nothing for `fromUrl` to overlay. That is asserted in the suite rather than
 * assumed here — if the route ever gains a parameter, the assertion is what
 * says this file needs an overlay.
 */
import type { ActiveSortDir } from './trackerToolbar';
import type { WorkingViewDefinition } from '@/stores/workingViewStore';

/** The Open Positions sort keys, in toolbar order. `default` is event time then
 *  ticker; the other two are live metrics the page already throttles. */
export const POSITION_SORT_KEYS = ['default', 'size', 'velocity'] as const;

export type PositionSortKey = (typeof POSITION_SORT_KEYS)[number];

/** The Open Positions sort, in the exact shape the view holds it — so the
 *  working-view field is a drop-in for the `useState` it replaces. */
export interface PositionSort {
  key: PositionSortKey;
  dir: ActiveSortDir;
}

export interface TrackerReading {
  positionSort: PositionSort;
}

/** The landing sort: event time, then ticker. Matches what the view seeded its
 *  `useState` with before this slice existed, so a trader with nothing
 *  remembered lands exactly where they always did. */
export const TRACKER_READING_DEFAULTS: TrackerReading = {
  positionSort: { key: 'default', dir: 'asc' },
};

function isSortKey(value: unknown): value is PositionSortKey {
  return typeof value === 'string' && (POSITION_SORT_KEYS as readonly string[]).includes(value);
}

export const trackerReading: WorkingViewDefinition<TrackerReading> = {
  id: 'tracker-reading-v1',
  parse(input) {
    if (!input || typeof input !== 'object') return null;
    const { positionSort } = input as Partial<TrackerReading>;
    if (!positionSort || typeof positionSort !== 'object') return null;
    const { key, dir } = positionSort as Partial<PositionSort>;
    if (!isSortKey(key) || (dir !== 'asc' && dir !== 'desc')) return null;
    // An allowlisted CLONE, field by field — never a spread of stored input.
    // Anything this file does not name cannot reach the view, so a record
    // written by an older or tampered-with shape carries nothing extra along.
    return { positionSort: { key, dir } };
  },
};
